"use client";
import { Img } from "@/components/Img";
import { Text } from "@/components/Text";
import { Column, Row } from "@/components/Stack";
import { StaticImport } from "next/dist/shared/lib/get-img-props";
import { GapValuesType } from "@/types/designSystem.types";
import { Box } from "@/components/Box";
import { Button } from "@/components/Button";
import {
  EventNamesEnum,
  PageViewedEventPageNameEnum,
} from "@/constants/constants";
import { useEffect } from "react";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";

interface ErrorScreenPageProps {
  imageSrc: string | StaticImport;
  imageAlt: string;
  mainText: string;
  description: string;
  buttonLabel: string;
  buttonLink: string;
  gap?: GapValuesType;
  onButtonClick?: () => void;
  errorPageName?: PageViewedEventPageNameEnum;
  errorString?: string;
}

export const Error = ({
  imageSrc,
  imageAlt,
  mainText,
  description,
  buttonLabel,
  buttonLink,
  gap = 0,
  onButtonClick,
  errorPageName = PageViewedEventPageNameEnum.globalErrorPage,
  errorString,
}: ErrorScreenPageProps) => {
  const { trackEvent } = useTrackingAnalytics();

  useEffect(() => {
    trackEvent({
      eventName: EventNamesEnum.ERROR_PAGE_VIEWED,
      properties: {
        page_name: errorPageName,
      },
    });

    // Send error details to Supabase for debugging
    if (errorString) {
      fetch("/api/send-to-supabase", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pool_number: -1,
          user_id: crypto.randomUUID(),
          event_name: "error_page_viewed_debug",
          event_value: errorString,
        }),
      }).catch(console.error);
    }
  }, []);

  return (
    <Row fullWidth={true} justify="center">
      <Box maxWidth="max-w-96">
        <Row justify="center">
          <Column align="center" gap={gap}>
            <div className="error-page-image-container">
              <Img src={imageSrc} alt={imageAlt} />
            </div>
            <Box paddingHorizontal={10}>
              <Column align="center" gap={1}>
                <Text
                  appearance="primary"
                  weight="medium"
                  alignment="center"
                  size={6}
                >
                  {mainText}
                </Text>
                <Text weight="medium" alignment="center" size={3}>
                  {description}
                </Text>
                <Box paddingTop={4} height="h-12" width="w-44">
                  <Button
                    onClick={onButtonClick}
                    replace={true}
                    href={buttonLink}
                    className="!bg-white text-black"
                  >
                    {buttonLabel}
                  </Button>
                </Box>
              </Column>
            </Box>
          </Column>
        </Row>
      </Box>
    </Row>
  );
};
